import { createSignal, For, type JSX, Show } from "solid-js";
import { createStore } from "solid-js/store";
import type { TovFieldKind } from "../../enums/tov";
import { TOV_FIELD_KIND } from "../../enums/tov";
import { Card } from "./primitives";

// Inline-edit primitives for TOV definitions. A definition row is edited in place via a
// field-spec-driven form, so each card type declares its fields once instead of
// hand-writing a form. Values not rendered as fields (e.g. displayOrder) are
// carried through unchanged from `initial`, so PUT full-replace stays complete.

export type FieldSpec = {
  name: string;
  label: string;
  kind: TovFieldKind;
  options?: readonly string[];
  optional?: boolean;
  grow?: boolean;
};

export type EditValues = Record<string, string | number | undefined>;

// Callbacks threaded from the Tov screen into every card so edit/delete share one
// guarded api call + refetch. `path` is the backend collection (e.g. "rules").
export type CardEditProps = {
  editable: boolean;
  onEdit: (path: string, id: string, body: EditValues) => void;
  onDelete: (path: string, id: string) => void;
};

function InlineEditForm(props: {
  fields: FieldSpec[];
  initial: EditValues;
  onSave: (values: EditValues) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = createStore<EditValues>({ ...props.initial });
  return (
    <form
      class="tov-form tov-edit-form"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave({ ...values });
      }}
    >
      <For each={props.fields}>
        {(field) => (
          <div class={`tov-field ${field.grow ? "grow" : ""}`}>
            <span class="tov-field-label">{field.label}</span>
            <Show
              when={field.kind === TOV_FIELD_KIND.SELECT}
              fallback={
                <input
                  class={`tov-input ${field.kind === TOV_FIELD_KIND.NUMBER ? "tov-input-num" : ""}`}
                  type={
                    field.kind === TOV_FIELD_KIND.NUMBER ? "number" : "text"
                  }
                  value={values[field.name] ?? ""}
                  onInput={(event) => {
                    const raw = event.currentTarget.value;
                    if (field.kind === TOV_FIELD_KIND.NUMBER) {
                      setValues(
                        field.name,
                        raw === "" && field.optional ? undefined : Number(raw),
                      );
                      return;
                    }
                    setValues(
                      field.name,
                      raw === "" && field.optional ? undefined : raw,
                    );
                  }}
                />
              }
            >
              <select
                class="tov-input"
                value={String(values[field.name] ?? "")}
                onChange={(event) =>
                  setValues(field.name, event.currentTarget.value)
                }
              >
                <For each={field.options ?? []}>
                  {(option) => <option value={option}>{option}</option>}
                </For>
              </select>
            </Show>
          </div>
        )}
      </For>
      <div class="tov-card-actions">
        <button type="submit" class="btn btn-primary">
          Save
        </button>
        <button type="button" class="btn" onClick={() => props.onCancel()}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// A definition card with edit/delete affordances. Read view is the children;
// toggling Edit swaps in the field-spec form. Controls only render when editable
// (draft Versions); active/archived Versions stay read-only.
export function EditableCard(props: {
  title: string;
  tag?: string;
  editable: boolean;
  fields: FieldSpec[];
  initial: EditValues;
  onSave: (values: EditValues) => void;
  onDelete: () => void;
  children: JSX.Element;
}) {
  const [editing, setEditing] = createSignal(false);
  return (
    <Card title={props.title} tag={props.tag}>
      <Show when={props.editable}>
        <div class="tov-card-actions">
          <button
            type="button"
            class="btn btn-sm"
            onClick={() => setEditing(!editing())}
          >
            {editing() ? "Close" : "Edit"}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            onClick={() => props.onDelete()}
          >
            Delete
          </button>
        </div>
      </Show>
      <Show when={editing()} fallback={props.children}>
        <InlineEditForm
          fields={props.fields}
          initial={props.initial}
          onSave={(values) => {
            props.onSave(values);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </Show>
    </Card>
  );
}

// A lighter edit affordance for nested rows (trait boundaries, channel placement
// children) that are not their own cards.
export function EditableRow(props: {
  editable: boolean;
  fields: FieldSpec[];
  initial: EditValues;
  onSave: (values: EditValues) => void;
  onDelete: () => void;
  children: JSX.Element;
}) {
  const [editing, setEditing] = createSignal(false);
  return (
    <Show
      when={!editing()}
      fallback={
        <InlineEditForm
          fields={props.fields}
          initial={props.initial}
          onSave={(values) => {
            props.onSave(values);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      }
    >
      <div class="tov-edit-row">
        {props.children}
        <Show when={props.editable}>
          <span class="tov-row-actions">
            <button
              type="button"
              class="btn btn-sm"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button
              type="button"
              class="btn btn-sm"
              onClick={() => props.onDelete()}
            >
              Delete
            </button>
          </span>
        </Show>
      </div>
    </Show>
  );
}
